import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useMemo, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileStates from 'FileStates';
import {type IntPropertyNode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import type {
  // eslint-disable-next-line camelcase
  MoleculeHeavyAtomCountQuery_molecules_MoleculeNode,
  MoleculeHeavyAtomCountQuery,
  MoleculeHeavyAtomCountQueryVariables,
} from 'model-hub/__generated/MoleculeHeavyAtomCountQuery';
import type {
  // eslint-disable-next-line camelcase
  ProteinAminoAcidsQuery_proteins_ProteinNode,
  ProteinAminoAcidsQuery,
  ProteinAminoAcidsQueryVariables,
} from 'model-hub/__generated/ProteinAminoAcidsQuery';
import {
  BoltzGenTargetType,
  MAX_BOLTZGEN_DESIGN_PROTEIN_LENGTH,
  MAX_BOLTZGEN_TARGET_MOLECULE_HEAVY_ATOM_COUNT,
  MAX_BOLTZGEN_TARGET_PROTEIN_LENGTH,
} from 'model-hub/generative-models/GenerativeModelsConstants';
import {runBoltzGen} from 'model-hub/generative-models/GenerativeModelsDAO';
import GenerativeModelsTabState from 'model-hub/generative-models/GenerativeModelsTabState';
import ComponentSearchModal from 'model-hub/scientific-models/input-forms/ComponentSearchModal';
import StickyFooter, {
  STICKY_FOOTER_CONTAINER_STYLES,
  STICKY_FOOTER_CONTENT_STYLES,
} from 'model-hub/StickyFooter';
import {FONT_PROXIMA_13, FONT_PROXIMA_17} from 'styles/FontConstants';
import PreviewBadge from 'ui-library/badges/PreviewBadge';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {GRAY_DARKER} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import NumericInput from 'ui-library/inputs/NumericInput';
import runModal from 'ui-library/runModal';
import {RowSelectionMode} from 'ui-library/scrollable/ScrollableTableConstants';
import APIUtil from 'util/APIUtil';
import * as logging from 'util/Logging';
import {assertNever, NumericInputChangeEvent} from 'util/Types';

import ModelHubExecutionCountNotice, {
  useGetRemainingExecutionsAndTenantName,
} from '../../../ModelHubExecutionCountNotice';
import {PROTEIN_AMINO_ACIDS_QUERY, MOLECULE_HEAVY_ATOM_COUNT_QUERY} from '../../../ModelHubQueries';

interface ValidationResult {
  isValid: boolean;
  validationMessage: string | null;
  isLoading: boolean;
}

interface BoltzgenTargetEntity {
  entityType: BoltzGenTargetType;
  entityId: string;
}

function getEntityTypeDisplayName(currentEntityType: BoltzGenTargetType): string {
  switch (currentEntityType) {
    case ModelTypeConstants.PROTEIN:
      return 'AA sequence';
    case ModelTypeConstants.MOLECULE:
      return 'Molecule';
    default:
      return assertNever(currentEntityType);
  }
}

function useTargetEntityValidation(
  currentEntityType: BoltzGenTargetType,
  targetId: string
): ValidationResult {
  // Always call both hooks, but skip the one we don't need
  const aaSequenceResult = useSkippableBenchlingQuery<
    ProteinAminoAcidsQuery,
    ProteinAminoAcidsQueryVariables
  >(PROTEIN_AMINO_ACIDS_QUERY, {
    variables: {ids: [targetId]},
    skip: currentEntityType !== ModelTypeConstants.PROTEIN,
  });

  const moleculeResult = useSkippableBenchlingQuery<
    MoleculeHeavyAtomCountQuery,
    MoleculeHeavyAtomCountQueryVariables
  >(MOLECULE_HEAVY_ATOM_COUNT_QUERY, {
    variables: {moleculeIds: [targetId]},
    skip: currentEntityType !== ModelTypeConstants.MOLECULE,
  });

  return useMemo(() => {
    if (currentEntityType === ModelTypeConstants.PROTEIN) {
      if (aaSequenceResult.type === QueryResultType.PENDING) {
        return {isValid: false, validationMessage: null, isLoading: true};
      }
      if (aaSequenceResult.type === QueryResultType.ERROR) {
        logging.warn('Failed to retrieve amino acids for target AA sequence.');
        return {
          isValid: false,
          validationMessage: 'Failed to retrieve amino acids for target AA sequence.',
          isLoading: false,
        };
      }
      // eslint-disable-next-line camelcase
      const aaSequence = aaSequenceResult.data?.proteins[0] as ProteinAminoAcidsQuery_proteins_ProteinNode;
      const aminoAcids = aaSequence.experimentalCurrentVersion.aminoAcids;

      if (aminoAcids.length < 1) {
        return {
          isValid: false,
          validationMessage: 'Cannot run BoltzGen on a target AA sequence without any amino acids.',
          isLoading: false,
        };
      }
      if (aminoAcids.length > MAX_BOLTZGEN_TARGET_PROTEIN_LENGTH) {
        return {
          isValid: false,
          validationMessage: `Cannot run BoltzGen on a target AA sequence with more than ${MAX_BOLTZGEN_TARGET_PROTEIN_LENGTH} amino acids.`,
          isLoading: false,
        };
      }
    }

    if (currentEntityType === ModelTypeConstants.MOLECULE) {
      if (moleculeResult.type === QueryResultType.PENDING) {
        return {isValid: false, validationMessage: null, isLoading: true};
      }
      if (moleculeResult.type === QueryResultType.ERROR) {
        return {
          isValid: false,
          validationMessage: 'Failed to retrieve molecule data.',
          isLoading: false,
        };
      }

      const molecule = moleculeResult.data
        ?.molecules[0] as MoleculeHeavyAtomCountQuery_molecules_MoleculeNode; // eslint-disable-line camelcase
      const heavyAtomCount =
        (molecule.currentVersion?.chemicalStructure?.heavyAtomCount as IntPropertyNode)?.value || 0;

      if (heavyAtomCount < 1) {
        return {
          isValid: false,
          validationMessage: 'Cannot run BoltzGen on a target molecule without any atoms.',
          isLoading: false,
        };
      }
      if (heavyAtomCount > MAX_BOLTZGEN_TARGET_MOLECULE_HEAVY_ATOM_COUNT) {
        return {
          isValid: false,
          validationMessage: `Cannot run BoltzGen on a target molecule with more than ${MAX_BOLTZGEN_TARGET_MOLECULE_HEAVY_ATOM_COUNT} heavy atoms.`,
          isLoading: false,
        };
      }
    }

    return {isValid: true, validationMessage: null, isLoading: false};
  }, [currentEntityType, aaSequenceResult, moleculeResult]);
}

async function handleTargetEntitySelection(onSelect: (entity: BoltzgenTargetEntity) => void): Promise<void> {
  const selectedEntityIds = (await runModal(ComponentSearchModal, {
    rowSelectionMode: RowSelectionMode.SINGLE,
    availableTypes: BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE
      ? [ModelTypeConstants.PROTEIN, ModelTypeConstants.MOLECULE]
      : [ModelTypeConstants.PROTEIN],
    title: 'Select target entity',
  })) as Array<string> | null | undefined;

  if (selectedEntityIds == null || selectedEntityIds.length === 0) {
    // Modal was closed without submitting
    return;
  }

  const selectedEntityId = selectedEntityIds[0];
  const entityType = APIUtil.apiIdToType(selectedEntityId);

  // Validate that the selected entity is a protein or molecule
  if (entityType === ModelTypeConstants.PROTEIN || entityType === ModelTypeConstants.MOLECULE) {
    onSelect({
      entityType: entityType as BoltzGenTargetType,
      entityId: selectedEntityId,
    });
  }
}

interface EntitySelectorProps {
  onSelect: (entity: BoltzgenTargetEntity) => void;
}

function EntitySelector({onSelect}: EntitySelectorProps): JSX.Element {
  const handleOpenSearch = async (): Promise<void> => {
    await handleTargetEntitySelection(onSelect);
  };

  return (
    <div className={css(styles.outerContainer)}>
      <div className={css(styles.content)}>
        <div className={css(styles.contentPadding)}>
          <div className={css(styles.titleContainer)}>
            <div className={css(styles.title)}>BoltzGen</div>
            <PreviewBadge />
            <ModelHubExecutionCountNotice />
          </div>
          <p className={css(styles.description)}>
            Select a target {getEntityTypeDisplayName(ModelTypeConstants.PROTEIN)} or{' '}
            {getEntityTypeDisplayName(ModelTypeConstants.MOLECULE).toLowerCase()} to generate new AA sequences
            that bind to this target. We'll show you the top-ranked result, and you'll be able to download the
            rest for further analysis. For more information about BoltzGen, see{' '}
            <a href="https://boltz.bio/boltzgen" target="_blank" rel="noopener noreferrer">
              here
            </a>
            .
          </p>
          <div className={css(styles.entitySelectorContainer)}>
            <div className={css(styles.label)}>Target Entity</div>
            <Button
              size={ButtonSize.MEDIUM}
              buttonStyle={ButtonStyle.TERTIARY_BLUE}
              text="Select target entity"
              onClick={handleOpenSearch}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

interface FormContentProps {
  targetEntity: BoltzgenTargetEntity;
  navigateToList?: () => void;
  onChangeTargetEntity?: (entity: BoltzgenTargetEntity | null) => void;
  onSubmit: () => void | Promise<void>;
}

export function BoltzGenFormContent({
  targetEntity,
  navigateToList,
  onChangeTargetEntity,
  onSubmit,
}: FormContentProps): JSX.Element {
  const [[rangeMin, rangeMax], setRange] = useState<[number, number]>([20, 50]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rangeValidationMessage, setRangeValidationMessage] = useState<string | null>(null);

  const {remainingExecutions} = useGetRemainingExecutionsAndTenantName();

  const {
    isValid: isTargetValid,
    validationMessage: targetValidationMessage,
    isLoading: isTargetEntityLoading,
  } = useTargetEntityValidation(targetEntity.entityType, targetEntity.entityId);

  let isValid = isTargetValid && rangeValidationMessage == null;
  let validationMessage = targetValidationMessage || rangeValidationMessage;

  if (remainingExecutions <= 0) {
    isValid = false;
    validationMessage = 'You have no remaining execution requests.';
  }

  if (isTargetEntityLoading) {
    isValid = false;
    validationMessage = 'Loading target entity...';
  }

  const validateAndSetRange = (rangeMin: number | null, rangeMax: number | null): void => {
    let message: string | null = null;

    if (rangeMin == null || rangeMax == null) {
      message = 'Minimum and maximum sequence lengths must be specified.';
    } else if (rangeMin < 1) {
      message = `Minimum sequence length must be at least 1.`;
    } else if (rangeMax > MAX_BOLTZGEN_DESIGN_PROTEIN_LENGTH) {
      message = `Maximum sequence length cannot exceed ${MAX_BOLTZGEN_DESIGN_PROTEIN_LENGTH}.`;
    } else if (rangeMin > rangeMax) {
      message = 'Minimum sequence length cannot be greater than maximum sequence length.';
    }

    setRangeValidationMessage(message);
    if (rangeMin != null && rangeMax != null) {
      setRange([rangeMin, rangeMax]);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    setIsSubmitting(true);
    const proteinIds = targetEntity.entityType === ModelTypeConstants.PROTEIN ? [targetEntity.entityId] : [];
    const moleculeIds =
      targetEntity.entityType === ModelTypeConstants.MOLECULE ? [targetEntity.entityId] : [];
    const success = await runBoltzGen({
      minLength: rangeMin,
      maxLength: rangeMax,
      proteinIds,
      moleculeIds,
    });
    setIsSubmitting(false);
    if (success) {
      await onSubmit();
    }
  };

  return (
    <div className={css(styles.outerContainer)}>
      <div className={css(styles.content)}>
        <div className={css(styles.contentPadding)}>
          {navigateToList && (
            <div className={css(styles.backButtonContainer)}>
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                size={ButtonSize.SMALL}
                icon="chevron-left"
                text="View all runs"
                onClick={navigateToList}
              />
            </div>
          )}
          <div className={css(styles.titleContainer)}>
            <div className={css(styles.title)}>BoltzGen</div>
            <PreviewBadge />
            <ModelHubExecutionCountNotice />
          </div>
          <p className={css(styles.description)}>
            Using the current {getEntityTypeDisplayName(targetEntity.entityType)} as a target, generate new AA
            sequences that bind to this target. We'll show you the top-ranked result, and you'll be able to
            download the rest for further analysis. For more information about BoltzGen, see{' '}
            <a href="https://boltz.bio/boltzgen" target="_blank" rel="noopener noreferrer">
              here
            </a>
            .
          </p>
          {onChangeTargetEntity && (
            <div className={css(styles.targetEntitySection)}>
              <div className={css(styles.label)}>Target Entity</div>
              <div className={css(styles.targetEntityRow)}>
                <BenchChip id={targetEntity.entityId} size={ChipSize.SMALL} isLink={false} />
                <Button
                  size={ButtonSize.MEDIUM}
                  buttonStyle={ButtonStyle.TERTIARY_BLUE}
                  text="Change"
                  onClick={async () => {
                    if (onChangeTargetEntity) {
                      await handleTargetEntitySelection(onChangeTargetEntity);
                    }
                  }}
                />
              </div>
            </div>
          )}
          <p className={css(styles.description)}>
            Choose how large you want the generated sequences to be. BoltzGen will randomly generate sequences
            between the min and max size.
          </p>
          <div className={css(styles.numericInputContainer)}>
            <div className={css(styles.numericInputRow)}>
              <div className={css(styles.label)}>Minimum Sequence Length</div>
              <NumericInput
                className={css(styles.numericInput)}
                value={rangeMin}
                required={true}
                onChange={(e: NumericInputChangeEvent) => {
                  validateAndSetRange(e.target.numericValue, rangeMax);
                }}
              />
            </div>
            <div className={css(styles.numericInputRow)}>
              <div className={css(styles.label)}>Maximum Sequence Length</div>
              <NumericInput
                className={css(styles.numericInput)}
                value={rangeMax}
                required={true}
                onChange={(e: NumericInputChangeEvent) => {
                  validateAndSetRange(rangeMin, e.target.numericValue);
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <StickyFooter>
        <Tooltip text={validationMessage} isDisabled={isValid}>
          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            size={ButtonSize.MEDIUM}
            text={isSubmitting ? 'Generating...' : 'Generate'}
            disabled={!isValid || isSubmitting}
            onClick={handleSubmit}
          />
        </Tooltip>
      </StickyFooter>
    </div>
  );
}

interface BoltzGenInputFormProps {
  inputTargetEntity?: BoltzgenTargetEntity | null;
  navigateToList?: () => void;
  onRequestClose?: () => void;
}

export const BoltzGenInputForm = ({
  inputTargetEntity,
  navigateToList,
  onRequestClose,
}: BoltzGenInputFormProps): JSX.Element => {
  const [targetEntity, setTargetEntity] = useState<BoltzgenTargetEntity | null>(inputTargetEntity ?? null);

  const navigateToEntity = async (): Promise<void> => {
    if (!targetEntity) {
      return;
    }
    const fileState = await FileStates.get().openFolderItem(targetEntity.entityId, {open: true});
    if (!fileState) {
      return;
    }
    const existingTab = fileState.findTab(GenerativeModelsTabState);
    if (existingTab != null) {
      existingTab.set({active: true});
    }
    onRequestClose?.();
  };

  useEffect(() => {
    if (inputTargetEntity) {
      setTargetEntity(inputTargetEntity);
    }
  }, [inputTargetEntity]);

  if (!targetEntity) {
    return <EntitySelector onSelect={setTargetEntity} />;
  }

  return (
    <BoltzGenFormContent
      targetEntity={targetEntity}
      navigateToList={navigateToList}
      onChangeTargetEntity={setTargetEntity}
      onSubmit={inputTargetEntity == null ? navigateToEntity : (navigateToList ?? (() => {}))}
    />
  );
};

const styles = StyleSheet.create({
  outerContainer: STICKY_FOOTER_CONTAINER_STYLES,
  content: STICKY_FOOTER_CONTENT_STYLES,
  contentPadding: {
    padding: '20px',
  },
  backButtonContainer: {
    marginBottom: '16px',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  title: {
    ...FONT_PROXIMA_17,
    fontWeight: 600,
    color: GRAY_DARKER,
  },
  description: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    marginTop: '8px',
  },
  label: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    fontWeight: 600,
  },
  entitySelectorContainer: {
    marginTop: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '400px',
  },
  targetEntitySection: {
    marginTop: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  targetEntityRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  sequenceSearchView: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  numericInputContainer: {
    marginTop: '24px',
    display: 'flex',
    flexDirection: 'column',
    width: '300px',
    justifyContent: 'space-between',
  },
  numericInputRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  numericInput: {
    width: '100px',
  },
});
